import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import {
  Box, TextField, Button, Typography,
  CircularProgress, IconButton, InputAdornment,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import FavoriteIcon from "@mui/icons-material/Favorite";
import { motion } from "framer-motion";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { setError("Uzupełnij wszystkie pola."); return; }
    setLoading(true); setError("");
    const ok = await login(email, password);
    setLoading(false);
    if (ok) navigate("/gallery");
    else setError("Nieprawidłowy e-mail, hasło lub konto wygasło.");
  };

  const inputSx = {
    mb: 1.5,
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px", bgcolor: "#fafafa",
      "& fieldset": { borderColor: "#dbdbdb" },
      "&:hover fieldset": { borderColor: "#bbb" },
      "&.Mui-focused fieldset": { borderColor: "#EB578A", borderWidth: 1.5 },
    },
    "& .MuiInputBase-input::placeholder": { color: "#aaa" },
  };

  return (
    <Box sx={{
      minHeight: "100vh", bgcolor: "#fff",
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      px: 3,
    }}>
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        style={{ width: "100%", maxWidth: 360 }}
      >
        {/* Logo */}
        <Box sx={{ textAlign: "center", mb: 5 }}>
          <Box sx={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            width: 80, height: 80, borderRadius: 24,
            background: "linear-gradient(135deg, #EB578A 0%, #C0145E 100%)",
            mb: 2.5, boxShadow: "0 10px 30px rgba(235,87,138,0.45)",
          }}>
            <FavoriteIcon sx={{ color: "#fff", fontSize: 40 }} />
          </Box>
          <Typography sx={{
            fontSize: 30, fontWeight: 900, letterSpacing: "-0.5px",
            background: "linear-gradient(90deg, #EB578A, #C0145E)",
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            fontFamily: "'Lato', Arial, sans-serif",
          }}>
            Księga Gości
          </Typography>
          <Typography sx={{ color: "#aaa", fontSize: 12, mt: 0.5, letterSpacing: 2, textTransform: "uppercase" }}>
            kalmarry.pl
          </Typography>
        </Box>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth placeholder="E-mail" type="email" variant="outlined"
            value={email} onChange={e => setEmail(e.target.value)}
            sx={inputSx}
          />
          <TextField
            fullWidth placeholder="Hasło" variant="outlined"
            type={showPass ? "text" : "password"}
            value={password} onChange={e => setPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPass(s => !s)} size="small" tabIndex={-1} edge="end">
                    {showPass
                      ? <VisibilityOffIcon fontSize="small" sx={{ color: "#aaa" }} />
                      : <VisibilityIcon fontSize="small" sx={{ color: "#aaa" }} />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={inputSx}
          />

          {error && (
            <Typography sx={{ color: "#e53935", fontSize: 13, mb: 1.5, textAlign: "center", fontWeight: 500 }}>
              {error}
            </Typography>
          )}

          <Button
            fullWidth type="submit" disabled={loading}
            sx={{
              mt: 0.5, py: 1.5,
              background: "linear-gradient(90deg, #EB578A 0%, #C0145E 100%)",
              color: "#fff", fontWeight: 700, fontSize: 15,
              borderRadius: "12px", textTransform: "none",
              boxShadow: "0 6px 20px rgba(235,87,138,0.4)",
              "&:hover": { background: "linear-gradient(90deg, #d94d7e 0%, #a81050 100%)" },
              "&.Mui-disabled": { background: "#f0c0d0", color: "#fff", boxShadow: "none" },
            }}
          >
            {loading ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : "Zaloguj się"}
          </Button>
        </form>

        {/* Register link */}
        <Box sx={{ mt: 4, pt: 3, borderTop: "1px solid #efefef", textAlign: "center" }}>
          <Typography sx={{ fontSize: 14, color: "#555" }}>
            Nie masz konta?{" "}
            <Box component={Link} to="/register" sx={{
              color: "#EB578A", fontWeight: 700, textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}>
              Zarejestruj się
            </Box>
          </Typography>
        </Box>
      </motion.div>
    </Box>
  );
}
